import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, UserPlus, Mail, FileText, CheckSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  parseVCard,
  parseURL,
  parseFile,
  parseText,
  detectShareType,
  type ParsedContact,
  type ParsedURL,
  type ParsedFile,
  type ParsedText
} from '@/lib/shareTargetParsers';
import { offlineQueue } from '@/lib/offlineQueue';

export default function Share() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shareType, setShareType] = useState<'contact' | 'file' | 'url' | 'text' | 'unknown'>('unknown');
  const [parsedData, setParsedData] = useState<ParsedContact | ParsedURL | ParsedFile | ParsedText | null>(null);

  useEffect(() => {
    const processShare = async () => {
      try {
        // Get share data from URL params
        const title = searchParams.get('title') || '';
        const text = searchParams.get('text') || '';
        const url = searchParams.get('url') || '';
        
        // Detect share type
        const type = detectShareType({ text, url });
        setShareType(type);

        // Parse based on type
        switch (type) {
          case 'contact': {
            const contact = parseVCard(text);
            setParsedData(contact);
            break;
          }
          case 'url': {
            const parsedURL = parseURL(url, title);
            setParsedData(parsedURL);
            break;
          }
          case 'text': {
            const parsedText = parseText(text);
            setParsedData(parsedText);
            break;
          }
          default:
            setParsedData({ text: text || url || title } as ParsedText);
        }
      } catch (error) {
        console.error('Share processing error:', error);
        toast.error('Failed to process shared content');
      } finally {
        setLoading(false);
      }
    };

    processShare();
  }, [searchParams]);

  const handleAddToContact = async () => {
    const contact = parsedData as ParsedContact;
    
    // Queue offline action
    await offlineQueue.enqueue({
      type: 'create',
      entity: 'contact',
      data: {
        name: contact.name || 'New Contact',
        email: contact.email,
        phone: contact.phone,
        company: contact.company,
        job_title: contact.jobTitle
      }
    });

    toast.success('Contact queued for creation');
    navigate('/contacts');
  };

  const handleCreateNote = async () => {
    const data = parsedData as ParsedText | ParsedURL;
    const noteText = 'text' in data ? data.text : data.url;

    toast.success('Note saved');
    navigate('/');
  };

  const handleCreateTask = async () => {
    const data = parsedData as ParsedText;
    
    navigate(`/tasks?action=create&title=${encodeURIComponent(data.text.slice(0, 100))}`);
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto p-4 min-h-screen flex items-center justify-center">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>What would you like to do?</CardTitle>
          <CardDescription>
            Choose an action for the shared content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Preview shared content */}
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">Shared content:</p>
            <div className="text-sm text-muted-foreground">
              {shareType === 'contact' && parsedData && (
                <div className="space-y-1">
                  {(parsedData as ParsedContact).name && <p><strong>Name:</strong> {(parsedData as ParsedContact).name}</p>}
                  {(parsedData as ParsedContact).email && <p><strong>Email:</strong> {(parsedData as ParsedContact).email}</p>}
                  {(parsedData as ParsedContact).phone && <p><strong>Phone:</strong> {(parsedData as ParsedContact).phone}</p>}
                  {(parsedData as ParsedContact).company && <p><strong>Company:</strong> {(parsedData as ParsedContact).company}</p>}
                </div>
              )}
              {shareType === 'url' && parsedData && (
                <div>
                  <p><strong>URL:</strong> {(parsedData as ParsedURL).url}</p>
                  {(parsedData as ParsedURL).title && <p><strong>Title:</strong> {(parsedData as ParsedURL).title}</p>}
                </div>
              )}
              {shareType === 'text' && parsedData && (
                <p className="whitespace-pre-wrap">{(parsedData as ParsedText).text}</p>
              )}
            </div>
          </div>

          {/* Action buttons based on share type */}
          <div className="space-y-2">
            {shareType === 'contact' && (
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={handleAddToContact}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add to Contacts
              </Button>
            )}

            {(shareType === 'text' || shareType === 'url') && (
              <>
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={handleCreateNote}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Save as Note
                </Button>

                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={handleCreateTask}
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Create Task
                </Button>
              </>
            )}

            {shareType === 'url' && parsedData && (parsedData as ParsedText).hasEmail && (
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate(`/inbox?action=compose&to=${(parsedData as ParsedText).extractedEmail}`)}
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            )}

            <Button
              className="w-full justify-start"
              variant="ghost"
              onClick={handleCancel}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
